// KB-PDF-UPLOAD: an uploaded document's text as Markdown, so the Knowledge base keeps a readable
// copy next to the original (people read it as a doc, agents through `read_doc`). Pure: the API
// reads the bytes (pdf.js for a PDF's text layer, an unzip for a DOCX) and hands the parts here.
//
// - PDF: pdf.js gives positioned text runs, not structure. Runs are grouped into lines by their
//   baseline; the body size is the most used font size (by characters); a short line set larger
//   is a heading (the sizes ranked: `#`, `##`, `###`); a line led by a bullet glyph or `1.` / `a)`
//   is a list item; lines whose runs sit in two or more columns, two or more rows running, are a
//   table; other lines flow into paragraphs, broken where the line gap widens. Page numbers and
//   lines repeated at the same place on most pages (running headers and footers) are dropped.
// - DOCX: `word/document.xml` walked in order: paragraph styles named "Title" / "heading N" are
//   headings, numbered paragraphs list items (bullet or numbered by `word/numbering.xml`), tables
//   pipe tables (a nested table's text stays in its cell).
// No OCR: a scanned PDF has no runs, and the caller reports "no text found".

import { rowsToPipeTable } from "../boards/markdown";

/** One text run of a PDF page as pdf.js places it (PDF user space: y grows upwards). */
export type PdfRun = {
  str: string;
  x: number;
  y: number;
  /** Advance width of the run. */
  w: number;
  /** Font size (the transform's vertical scale, or the run's height). */
  size: number;
};

type Line = {
  y: number;
  x: number;
  size: number;
  cells: string[];
  text: string;
  page: number;
  runs: PdfRun[];
};

const BULLET = /^[•●○◦▪■□▫‣⁃∙·–—*-]\s*/;
const NUMBERED = /^(\d{1,3}|[a-zA-Z])[.)]\s+/;
const PAGE_NUMBER = /^(page\s+)?\d{1,4}(\s*(of|\/)\s*\d{1,4})?$/i;

const round = (n: number) => Math.round(n * 2) / 2;
const clean = (s: string) => s.replace(/\s+/g, " ").trim();

/** A page's runs as lines, top to bottom; a line's cells split where runs sit far apart. */
function linesOf(runs: readonly PdfRun[], page: number): Line[] {
  const kept = runs.filter((r) => r.str.trim() !== "" && Number.isFinite(r.y));
  const sorted = [...kept].sort((a, b) => b.y - a.y || a.x - b.x);
  const groups: PdfRun[][] = [];
  for (const r of sorted) {
    const g = groups.at(-1);
    const ref = g?.[0];
    if (g && ref && Math.abs(ref.y - r.y) <= Math.max(1, 0.4 * Math.max(ref.size, r.size)))
      g.push(r);
    else groups.push([r]);
  }
  return groups.map((g) => {
    g.sort((a, b) => a.x - b.x);
    const size = round(Math.max(...g.map((r) => r.size)));
    const cells: string[] = [];
    let cur = "";
    let end = Number.NEGATIVE_INFINITY;
    for (const r of g) {
      const gap = r.x - end;
      if (cur !== "" && gap > Math.max(0.8 * size, 5)) {
        cells.push(clean(cur));
        cur = r.str;
      } else if (cur !== "" && gap > 0.15 * size && !/\s$/.test(cur) && !/^\s/.test(r.str))
        cur += ` ${r.str}`;
      else cur += r.str;
      end = Math.max(end, r.x + r.w);
    }
    if (cur.trim() !== "") cells.push(clean(cur));
    return {
      y: g[0]?.y ?? 0,
      x: g[0]?.x ?? 0,
      size,
      cells,
      text: cells.join(" "),
      page,
      runs: g,
    };
  });
}

/**
 * A table's rows by column: the columns start where the widest row's cells do, and every row's
 * runs go to the column they start in (a cell that ran into its neighbour is split again).
 */
function tableRows(lines: readonly Line[]): string[][] {
  const widest = lines.reduce((a, b) => (b.cells.length > a.cells.length ? b : a));
  const starts: number[] = [];
  let end = Number.NEGATIVE_INFINITY;
  for (const r of widest.runs) {
    if (r.x - end > Math.max(0.8 * widest.size, 5) || starts.length === 0) starts.push(r.x);
    end = Math.max(end, r.x + r.w);
  }
  return lines.map((l) => {
    const cols: string[][] = starts.map(() => []);
    for (const r of l.runs) {
      let c = 0;
      for (let k = 0; k < starts.length; k++)
        if (r.x >= (starts[k] as number) - 0.5 * l.size) c = k;
      cols[c]?.push(r.str);
    }
    return cols.map((parts) => clean(parts.join(" ")));
  });
}

/** Running headers and footers: the same text (digits aside) at about the same height on most pages. */
function repeatedKeys(pages: Line[][]): Set<string> {
  if (pages.length < 3) return new Set();
  const seen = new Map<string, number>();
  for (const lines of pages) {
    const keys = new Set(
      [...lines.slice(0, 2), ...lines.slice(-2)].map(
        (l) => `${Math.round(l.y / 4)}:${l.text.replace(/\d+/g, "#").toLowerCase()}`,
      ),
    );
    for (const k of keys) seen.set(k, (seen.get(k) ?? 0) + 1);
  }
  const min = Math.max(3, Math.ceil(pages.length * 0.6));
  return new Set([...seen].filter(([, n]) => n >= min).map(([k]) => k));
}

/** The most used size by characters. */
function bodySize(lines: readonly Line[]): number {
  const weight = new Map<number, number>();
  for (const l of lines) weight.set(l.size, (weight.get(l.size) ?? 0) + l.text.length);
  let best = 0;
  let most = -1;
  for (const [s, n] of weight)
    if (n > most || (n === most && s < best)) {
      best = s;
      most = n;
    }
  return best;
}

/** The usual line step: the lower quartile of the gaps (paragraph breaks are the wide ones). */
function lowerQuartile(xs: number[]): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.floor(s.length / 4)] as number;
}

/** A pipe table (the first row is the header; short rows padded). */
const pipeTable = rowsToPipeTable;

/** A PDF's pages (their runs) as Markdown; "" when there is no text (a scanned PDF). */
export function pdfToMarkdown(pages: readonly (readonly PdfRun[])[]): string {
  const perPage = pages.map((runs, i) => linesOf(runs, i));
  const repeated = repeatedKeys(perPage);
  const lines = perPage
    .flat()
    .filter(
      (l) =>
        !PAGE_NUMBER.test(l.text) &&
        !repeated.has(`${Math.round(l.y / 4)}:${l.text.replace(/\d+/g, "#").toLowerCase()}`),
    );
  if (lines.length === 0) return "";
  const body = bodySize(lines);
  const headingSizes = [
    ...new Set(
      lines.filter((l) => l.size >= body * 1.2 && l.text.length <= 150).map((l) => l.size),
    ),
  ].sort((a, b) => b - a);
  const levelOf = (size: number) => Math.min(3, headingSizes.indexOf(size) + 1);
  const gaps: number[] = [];
  for (let i = 1; i < lines.length; i++) {
    const a = lines[i - 1] as Line;
    const b = lines[i] as Line;
    if (a.page === b.page && a.size === body && b.size === body && a.y > b.y) gaps.push(a.y - b.y);
  }
  const step = lowerQuartile(gaps) || body * 1.2;
  // The body's left margin: where most body-size lines start.
  const starts = new Map<number, number>();
  for (const l of lines)
    if (l.size === body) starts.set(Math.round(l.x), (starts.get(Math.round(l.x)) ?? 0) + 1);
  const margin = [...starts].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0]?.[0] ?? 0;

  const out: string[] = [];
  let para: string[] = [];
  let item: string | null = null;
  let itemX = 0;
  const join = (parts: string[], next: string) => {
    const last = parts.at(-1);
    if (last !== undefined && /[A-Za-z]-$/.test(last) && /^[a-z]/.test(next))
      parts[parts.length - 1] = last.slice(0, -1) + next;
    else parts.push(next);
  };
  const flushPara = () => {
    if (para.length) out.push("", para.join(" "), "");
    para = [];
  };
  const flushItem = () => {
    if (item !== null) out.push(item);
    item = null;
  };
  const flush = () => {
    flushPara();
    flushItem();
  };

  for (let i = 0; i < lines.length; i++) {
    const l = lines[i] as Line;
    const prev = lines[i - 1];
    const gap = prev && prev.page === l.page ? prev.y - l.y : Number.POSITIVE_INFINITY;
    // A table: this and the next line both split into the same number (≥ 2) of cells.
    if (l.cells.length >= 2) {
      const rows: Line[] = [l];
      let j = i + 1;
      while (j < lines.length) {
        const n = lines[j] as Line;
        if (n.cells.length < 2 || Math.abs(n.cells.length - l.cells.length) > 1) break;
        rows.push(n);
        j++;
      }
      if (rows.length >= 2) {
        flush();
        out.push("", pipeTable(tableRows(rows)), "");
        i = j - 1;
        continue;
      }
    }
    const text = l.text;
    if (headingSizes.includes(l.size) && l.size >= body * 1.2 && text.length <= 150) {
      flush();
      const level = levelOf(l.size);
      // A heading set on two lines: one heading.
      const last = out.at(-2);
      if (prev && prev.size === l.size && gap < l.size * 1.6 && last?.startsWith("#"))
        out[out.length - 2] = `${last} ${text}`;
      else out.push("", `${"#".repeat(level)} ${text}`, "");
      continue;
    }
    if (BULLET.test(text) && text.replace(BULLET, "") !== "") {
      flush();
      item = `- ${text.replace(BULLET, "")}`;
      itemX = l.x;
      continue;
    }
    const numbered = NUMBERED.exec(text);
    if (numbered && text.length > numbered[0].length) {
      flush();
      item = `${/^\d+$/.test(numbered[1] as string) ? numbered[1] : "1"}. ${text.slice(numbered[0].length)}`;
      itemX = l.x;
      continue;
    }
    // A list item's next line (indented past its marker, or at its text and starting lower
    // case, no wider gap): the same item.
    if (
      item !== null &&
      gap <= step * 1.4 &&
      (l.x > itemX + 1 || (Math.abs(l.x - itemX) <= 1 && /^[a-z]/.test(text)))
    ) {
      const parts: string[] = [item];
      join(parts, text);
      item = parts.join(" ");
      continue;
    }
    // Bullets drawn as graphics (no glyph in the text): two or more lines running indented
    // past the body's margin at the same place are list items.
    const indented = (n: Line | undefined) =>
      n !== undefined &&
      n.page === l.page &&
      n.size === body &&
      Math.abs(n.x - l.x) <= 1 &&
      l.x >= margin + body * 0.9;
    if (l.size === body && (indented(prev) || indented(lines[i + 1]))) {
      flush();
      item = `- ${text}`;
      itemX = l.x;
      continue;
    }
    flushItem();
    if (para.length && gap > step * 1.4) flushPara();
    join(para, text);
  }
  flush();
  return tidy(out.join("\n"));
}

const tidy = (md: string) =>
  md
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

// DOCX ---------------------------------------------------------------------------------------

const ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
};

function decode(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) => {
    if (e[0] === "#") {
      const n = e[1] === "x" || e[1] === "X" ? Number.parseInt(e.slice(2), 16) : Number(e.slice(1));
      return Number.isFinite(n) ? String.fromCodePoint(n) : m;
    }
    return ENTITIES[e.toLowerCase()] ?? m;
  });
}

const attr = (tag: string, name: string) => new RegExp(`${name}="([^"]*)"`).exec(tag)?.[1] ?? null;

/** `styleId` → its name, from `word/styles.xml`. */
function styleNames(xml: string | null): Map<string, string> {
  const out = new Map<string, string>();
  if (!xml) return out;
  for (const m of xml.matchAll(/<w:style\b[^>]*>[\s\S]*?<\/w:style>/g)) {
    const id = attr(m[0], "w:styleId");
    const name = /<w:name\b[^>]*w:val="([^"]*)"/.exec(m[0])?.[1];
    if (id && name) out.set(id, name);
  }
  return out;
}

/** `numId:ilvl` → whether that level is a bullet, from `word/numbering.xml`. */
function bulletLevels(xml: string | null): (numId: string, ilvl: string) => boolean {
  if (!xml) return () => true;
  const abstract = new Map<string, Map<string, string>>();
  for (const m of xml.matchAll(/<w:abstractNum\b[^>]*>[\s\S]*?<\/w:abstractNum>/g)) {
    const id = attr(m[0], "w:abstractNumId");
    if (!id) continue;
    const lvls = new Map<string, string>();
    for (const l of m[0].matchAll(/<w:lvl\b[^>]*>[\s\S]*?<\/w:lvl>/g)) {
      const ilvl = attr(l[0], "w:ilvl");
      const fmt = /<w:numFmt\b[^>]*w:val="([^"]*)"/.exec(l[0])?.[1];
      if (ilvl && fmt) lvls.set(ilvl, fmt);
    }
    abstract.set(id, lvls);
  }
  const nums = new Map<string, string>();
  for (const m of xml.matchAll(/<w:num\b[^>]*>[\s\S]*?<\/w:num>/g)) {
    const id = attr(m[0], "w:numId");
    const abs = /<w:abstractNumId\b[^>]*w:val="([^"]*)"/.exec(m[0])?.[1];
    if (id && abs) nums.set(id, abs);
  }
  return (numId, ilvl) => {
    const fmt = abstract.get(nums.get(numId) ?? "")?.get(ilvl);
    return fmt === undefined || fmt === "bullet" || fmt === "none";
  };
}

function headingLevel(name: string | undefined): number | null {
  if (!name) return null;
  if (/^title$/i.test(name)) return 1;
  const h = /^heading\s*(\d)$/i.exec(name);
  return h ? Math.min(3, Number(h[1])) : null;
}

/** A DOCX's parts as Markdown; "" when the document has no text. */
export function docxToMarkdown(parts: {
  document: string;
  styles?: string | null;
  numbering?: string | null;
}): string {
  const names = styleNames(parts.styles ?? null);
  const isBullet = bulletLevels(parts.numbering ?? null);
  const out: string[] = [];
  let tableDepth = 0;
  let rows: string[][] = [];
  let row: string[] | null = null;
  let cellText: string[] | null = null;
  // the paragraph being read
  let text = "";
  let style: string | null = null;
  let num: { id: string; ilvl: string } | null = null;
  let inText = false;
  let inPPr = false;
  let numId = "";
  let ilvl = "0";
  let inNumPr = false;
  let counters = new Map<string, number>();

  const endParagraph = () => {
    const t = clean(text);
    text = "";
    if (cellText) {
      if (t) cellText.push(t);
    } else if (t) {
      const level = headingLevel(style ? (names.get(style) ?? style) : undefined);
      if (level) out.push("", `${"#".repeat(level)} ${t}`, "");
      else if (num) {
        const indent = "  ".repeat(Math.min(4, Number(num.ilvl) || 0));
        if (isBullet(num.id, num.ilvl)) out.push(`${indent}- ${t}`);
        else {
          const k = `${num.id}:${num.ilvl}`;
          const n = (counters.get(k) ?? 0) + 1;
          counters.set(k, n);
          out.push(`${indent}${n}. ${t}`);
        }
      } else {
        counters = new Map();
        out.push("", t, "");
      }
    }
    style = null;
    num = null;
  };

  for (const m of parts.document.matchAll(/<(\/?)([\w:]+)([^>]*?)(\/?)>|([^<]+)/g)) {
    const [, close, tag, attrs = "", selfClose, chars] = m;
    if (chars !== undefined) {
      if (inText) text += decode(chars);
      continue;
    }
    const opening = close !== "/";
    switch (tag) {
      case "w:t":
        inText = opening && selfClose !== "/";
        break;
      case "w:tab":
        if (opening) text += " ";
        break;
      case "w:br":
      case "w:cr":
        if (opening) text += " ";
        break;
      case "w:pPr":
        inPPr = opening && selfClose !== "/";
        break;
      case "w:pStyle":
        if (inPPr) style = attr(attrs, "w:val");
        break;
      case "w:numPr":
        inNumPr = opening && selfClose !== "/";
        if (inNumPr) {
          numId = "";
          ilvl = "0";
        } else if (inPPr && numId && numId !== "0") num = { id: numId, ilvl };
        break;
      case "w:numId":
        if (inNumPr) numId = attr(attrs, "w:val") ?? "";
        break;
      case "w:ilvl":
        if (inNumPr) ilvl = attr(attrs, "w:val") ?? "0";
        break;
      case "w:p":
        if (opening && selfClose !== "/") {
          text = "";
          style = null;
          num = null;
        } else endParagraph();
        break;
      case "w:tbl":
        if (opening) {
          tableDepth++;
          if (tableDepth === 1) {
            rows = [];
            counters = new Map();
          }
        } else {
          tableDepth--;
          if (tableDepth === 0 && rows.length) {
            out.push("", rows.length === 1 ? (rows[0]?.join(" · ") ?? "") : pipeTable(rows), "");
            rows = [];
          }
        }
        break;
      case "w:tr":
        if (tableDepth !== 1) break;
        if (opening) row = [];
        else if (row) {
          if (row.some((c) => c !== "")) rows.push(row);
          row = null;
        }
        break;
      case "w:tc":
        if (tableDepth !== 1) break;
        if (opening) cellText = [];
        else if (cellText) {
          row?.push(cellText.join(" "));
          cellText = null;
        }
        break;
    }
  }
  return tidy(out.join("\n"));
}

/** Plain text as Markdown: line endings normalised, outer blank lines trimmed. */
export function textToMarkdown(text: string): string {
  return text.replace(/^﻿/, "").replace(/\r\n?/g, "\n").trim();
}
